import * as i0 from '@angular/core';
import { Injectable, NgModule } from '@angular/core';
import * as i1 from '@angular/common/http';
import { HttpClientModule } from '@angular/common/http';
import BroadcastChannel from 'broadcast-channel';
import { BehaviorSubject } from 'rxjs';
import { filter, tap, mergeMap, switchMap } from 'rxjs/operators';

var ProductType;
(function (ProductType) {
    ProductType["HOTEL"] = "HOTEL";
    ProductType["FLIGHT"] = "FLIGHT";
    ProductType["HEALTH_INSURANCE"] = "HEALTH_INSURANCE";
    ProductType["ACTIVITY"] = "ACTIVITY";
    ProductType["PACKAGE"] = "PACKAGE";
    ProductType["CAR"] = "CAR";
    ProductType["TOUR"] = "TOUR";
})(ProductType || (ProductType = {}));

var TripChannelTypes;
(function (TripChannelTypes) {
    TripChannelTypes["TRIP_UPDATE"] = "TRIP_UPDATE";
    TripChannelTypes["TRIP_OPEN"] = "TRIP_OPEN";
    TripChannelTypes["PRODUCT_ADD"] = "PRODUCT_ADD";
    TripChannelTypes["PRODUCT_DELETE"] = "PRODUCT_DELETE";
    TripChannelTypes["PRODUCT_UPDATE"] = "PRODUCT_UPDATE";
})(TripChannelTypes || (TripChannelTypes = {}));

const TRIPS_CHANNEL = 'AmTripsUpdate';
class AmTripsUpdateSyncService {
    constructor(zone) {
        this.zone = zone;
        this._tripUpdates$ = new BehaviorSubject(null);
        this._productUpdates$ = new BehaviorSubject(null);
        this._tripChannel = new BroadcastChannel(TRIPS_CHANNEL);
        this._tripChannel.onmessage = message => this.onMessageHandler(message);
        this.tripUpdates$ = this._tripUpdates$.pipe(filter(value => !!value));
        this.productUpdates$ = this._productUpdates$.pipe(filter(value => !!value));
    }
    onMessageHandler(message) {
        this.zone.run(() => {
            switch (message.type) {
                case TripChannelTypes.TRIP_OPEN:
                    this._tripUpdates$.next(message);
                    break;
                case TripChannelTypes.TRIP_UPDATE:
                    this._tripUpdates$.next(message);
                    break;
                case TripChannelTypes.PRODUCT_ADD:
                    this._productUpdates$.next(message);
                    break;
                case TripChannelTypes.PRODUCT_DELETE:
                    this._productUpdates$.next(message);
                    break;
            }
        });
    }
    updateTrip(tripId, editPayload) {
        const updateTripMessage = {
            type: TripChannelTypes.TRIP_UPDATE,
            tripId,
            payload: editPayload
        };
        this._tripChannel.postMessage(updateTripMessage);
    }
    openTrip(tripId) {
        const updateTripMessage = {
            type: TripChannelTypes.TRIP_OPEN,
            tripId
        };
        this._tripChannel.postMessage(updateTripMessage);
    }
    addProduct(tripId, productId, productType, brand, currencyCode) {
        const updateTripMessage = {
            type: TripChannelTypes.PRODUCT_ADD,
            tripId,
            payload: {
                productId,
                productType,
                brand,
                currencyCode
            }
        };
        this._tripChannel.postMessage(updateTripMessage);
    }
    deleteProduct(tripId, productId, productType) {
        const updateTripMessage = {
            type: TripChannelTypes.PRODUCT_DELETE,
            tripId,
            payload: {
                productId,
                productType
            }
        };
        this._tripChannel.postMessage(updateTripMessage);
    }
}
/** @nocollapse */ AmTripsUpdateSyncService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AmTripsUpdateSyncService, deps: [{ token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ AmTripsUpdateSyncService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AmTripsUpdateSyncService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AmTripsUpdateSyncService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i0.NgZone }]; } });

class FlightResolver {
    transformToPayload(product, destinationCode) {
        return {
            cityCode: destinationCode,
            externalId: product.id
        };
    }
    isDestinationProduct() {
        return true;
    }
}
class InsuranceResolver {
    transformToPayload(product, destinationCode) {
        return {
            cityCode: destinationCode,
            externalId: product.id
        };
    }
    isDestinationProduct() {
        return false;
    }
}
class HotelResolver {
    transformToPayload(product, destinationCode) {
        return {
            cityCode: destinationCode,
            externalId: product.id,
            checkInDateTime: product.checkin,
            checkOutDateTime: product.checkout,
            room: product.room,
            checkinTime: product.checkin_time,
            checkoutTime: product.checkout_time
        };
    }
    isDestinationProduct() {
        return true;
    }
}
class ActivityResolver {
    transformToPayload(product, destinationCode) {
        return {
            cityCode: destinationCode,
            externalId: product.activity_id,
            endTourDay: product.end_tour_day,
            modalityId: product.modality_id,
            pickUpId: product.pick_up_id,
            adultsQty: product.qty_adult,
            childrenQty: product.qty_child,
            infantsQty: product.qty_infant,
            scheduleId: product.schedule_id,
            seasonId: product.season_id,
            tourDay: product.tour_day,
            paxType: product.pax_type,
            days: product.days,
            destinationCode: product.destination_code
        };
    }
    isDestinationProduct() {
        return true;
    }
}
class PackageResolver {
    transformToPayload(product) {
        return {
            ...product
        };
    }
    isDestinationProduct() {
        return false;
    }
}
class CarResolver {
    transformToPayload(product, cityCode) {
        return {
            ...product,
            cityCode
        };
    }
    isDestinationProduct() {
        return true;
    }
}
class TourResolver {
    transformToPayload(product) {
        return {
            ...product
        };
    }
    isDestinationProduct() {
        return false;
    }
}
const productResolverMap = new Map([
    [ProductType.HOTEL, new HotelResolver()],
    [ProductType.FLIGHT, new FlightResolver()],
    [ProductType.ACTIVITY, new ActivityResolver()],
    [ProductType.PACKAGE, new PackageResolver()],
    [ProductType.CAR, new CarResolver()],
    [ProductType.TOUR, new TourResolver()],
    [ProductType.HEALTH_INSURANCE, new InsuranceResolver()]
]);
class ProductResolverFactory {
    static getResolver(productType) {
        return productResolverMap.get(productType);
    }
}

const TRIPS_API_URL = '/v2/trips/api/trips'; // URL to web api
const TRACKING_API_URL = '/v2/trips/api/trackings'; // URL to web api
const productTypeToPath = new Map([
    [ProductType.HOTEL, 'hotels'],
    [ProductType.FLIGHT, 'flights'],
    [ProductType.HEALTH_INSURANCE, 'insurances'],
    [ProductType.ACTIVITY, 'activities'],
    [ProductType.PACKAGE, 'packages'],
    [ProductType.CAR, 'cars'],
    [ProductType.TOUR, 'tours']
]);
class AmTripsUpdateService {
    constructor(http, tripSyncService) {
        this.http = http;
        this.tripSyncService = tripSyncService;
        this.tripUpdates$ = this.tripSyncService.tripUpdates$;
        this.productsUpdates$ = this.tripSyncService.productUpdates$;
    }
    addProduct(tripId, productType, newProductPayload, brand, currencyCode, availabilityId) {
        const url = this.resolveProductBasePath(tripId, productType);
        const availabilityIdNew = availabilityId || newProductPayload.availabilityId;
        const payload = {
            ...newProductPayload,
            brand,
            currencyCode,
            availabilityId: availabilityIdNew
        };
        return this.http
            .post(url, payload, { params: { brand, currencyCode } })
            .pipe(tap((res) => {
            if (availabilityId) {
                this.createTrackingInProduct(tripId, res.id, availabilityId, productType, currencyCode).subscribe(value => {
                    console.log('response de traking ', value);
                }, error => {
                    console.log('error tracking ', error);
                });
            }
            this.tripSyncService.addProduct(tripId, res.id, productType, brand, currencyCode);
        }));
    }
    deleteProduct(tripId, productType, productId) {
        const url = `${this.resolveProductBasePath(tripId, productType)}/${productId}`;
        return this.http
            .delete(url)
            .pipe(tap(_ => this.tripSyncService.deleteProduct(tripId, productId, productType)));
    }
    // TODO: returns Observable<Trips>
    editTrip(id, body) {
        return this.http
            .patch(`${TRIPS_API_URL}/${id}`, body)
            .pipe(tap(_ => this.tripSyncService.updateTrip(id, body)));
    }
    createTrip(body) {
        return this.http
            .post(TRIPS_API_URL, body)
            .pipe(tap(trip => this.tripSyncService.updateTrip(trip.id, trip)));
    }
    openTrip(id) {
        return this.http
            .get(`${TRIPS_API_URL}/${id}`)
            .pipe(tap(_ => this.tripSyncService.openTrip(id)));
    }
    getCurrentTripId() {
        return this.http.get(`${TRIPS_API_URL}/current`);
    }
    createTrackingInProduct(tripId, productId, availabilityId, productType, currencyCode) {
        let body = {
            tripId: tripId,
            currencyCode,
            productAvailabilities: [
                {
                    productId: productId,
                    availabilityId: availabilityId,
                    productType: productType,
                    trackingType: 'PRODUCT'
                }
            ]
        };
        return this.http.post(`${TRACKING_API_URL}`, body);
    }
    addProductInTripToCheckout(productType, newProductPayload, availabilityId, currencyCode) {
        return this.getCurrentTrip().pipe(mergeMap(respCurrent => {
            let productResolver = ProductResolverFactory.getResolver(productType);
            const productPayload = productResolver.transformToPayload(newProductPayload, respCurrent.destinations[0].code);
            return this.addProduct(respCurrent.id, productType, productPayload, respCurrent.brand, currencyCode, availabilityId);
        }));
    }
    getCurrentTrip() {
        return this.http
            .get(`${TRIPS_API_URL}/current`)
            .pipe(switchMap((res) => this.http.get(`${TRIPS_API_URL}/${res.tripId}`)));
    }
    resolveProductBasePath(tripId, productType) {
        return `${TRIPS_API_URL}/${tripId}/products/${productTypeToPath.get(productType.toUpperCase())}`;
    }
}
/** @nocollapse */ AmTripsUpdateService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AmTripsUpdateService, deps: [{ token: i1.HttpClient }, { token: AmTripsUpdateSyncService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ AmTripsUpdateService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AmTripsUpdateService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AmTripsUpdateService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: AmTripsUpdateSyncService }]; } });

class AmTripsUpdateServiceModule {
}
/** @nocollapse */ AmTripsUpdateServiceModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AmTripsUpdateServiceModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ AmTripsUpdateServiceModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: AmTripsUpdateServiceModule, imports: [HttpClientModule] });
/** @nocollapse */ AmTripsUpdateServiceModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AmTripsUpdateServiceModule, providers: [AmTripsUpdateService], imports: [HttpClientModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AmTripsUpdateServiceModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [HttpClientModule],
                    providers: [AmTripsUpdateService]
                }]
        }] });

/*
 * Public API Surface of am-trips-update-service
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AmTripsUpdateService, AmTripsUpdateServiceModule, ProductType, TripChannelTypes };
